import React, { useEffect, useState } from 'react';
import { message } from 'antd';
import dayjs from 'dayjs';
import {
  EmptyState,
  SectionCard,
  StatusBadge,
  TWButton,
  TWInput,
  TWSelect,
} from '../../../components/ui';
import fleetApi from '../../../services/fleetApi';
import kitchenterminalApi from '../../../services/kitchenterminalApi';
import terminalApi from '../../../services/terminalApi';
import { fleetErrorText } from '../use-fleet-poll';
import { APPS, ROLES, formatBytes, isHttpUrl, relativeSeen } from '../fleet-utils';
import CompanyStoreSelect from './company-store-select';

const DASH = '—';
const isNil = (v) => v === undefined || v === null;

// A named card whose title is also its accessible name.
const Card = ({ title, actions, children }) => (
  <div role="group" aria-label={title}>
    <SectionCard title={title} actions={actions}>
      {children}
    </SectionCard>
  </div>
);

const Dl = ({ rows }) => (
  <dl className="grid grid-cols-[8rem_minmax(0,1fr)] gap-x-4 gap-y-2 text-sm">
    {rows.map(([label, value]) => (
      <React.Fragment key={label}>
        <dt className="text-slate-500 dark:text-slate-400">{label}</dt>
        <dd className="min-w-0 break-words text-slate-900 dark:text-slate-100">{value}</dd>
      </React.Fragment>
    ))}
  </dl>
);

const toOptions = (rows) => (rows || []).map((r) => ({ value: r.id, label: r.name }));

const packageLabel = (pkg) => APPS.find((a) => a.packagename === pkg)?.label || pkg;

const parseTags = (text) =>
  text
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);

// Only strict booleans from the agent raise a flag; null, undefined, 0 and ''
// never do (P-20).
const adbOn = (telemetry) => {
  const adb = telemetry?.adb;
  if (!adb || typeof adb !== 'object') return false;
  return ['enabled', 'net', 'tls', 'port5555'].some((key) => adb[key] === true);
};

const DeviceCard = ({ device }) => {
  const tags = device.tags || [];
  return (
    <Card title="Device">
      <Dl
        rows={[
          ['Serial', device.serialnumber || DASH],
          ['Model', device.model || DASH],
          ['Firmware', device.fingerprint || DASH],
          [
            'Agent',
            device.agentversionname && !isNil(device.agentversioncode)
              ? `${device.agentversionname} (${device.agentversioncode})`
              : DASH,
          ],
          ['Enrolled', device.enrolledat ? dayjs(device.enrolledat).format('MMM D, YYYY HH:mm') : DASH],
          [
            'Tags',
            tags.length ? (
              <span className="flex flex-wrap gap-1">
                {tags.map((tag) => (
                  <StatusBadge key={tag} size="sm" tone="slate" label={tag} />
                ))}
              </span>
            ) : (
              DASH
            ),
          ],
        ]}
      />
    </Card>
  );
};

const TelemetryCard = ({ device }) => {
  const t = device.telemetry;
  const has = (value) => Boolean(t) && !isNil(value);

  let wifi = DASH;
  if (t && (!isNil(t.wifissid) || !isNil(t.wifirssi))) {
    const ssid = isNil(t.wifissid) ? null : t.wifissid;
    const rssi = isNil(t.wifirssi) ? null : t.wifirssi + ' dBm';
    wifi = [ssid, rssi].filter(Boolean).join(' · ');
  }
  let locked = DASH;
  if (has(t?.lockedapprunning)) {
    const pkg = t.lockedpackage ?? device.lockedpackage;
    locked = (t.lockedapprunning ? 'Running' : 'Not running') + (pkg ? ' · ' + pkg : '');
  }
  const lastBoot = device.lastbootat ?? t?.lastbootat;

  const rows = [
    [
      'Battery',
      has(t?.battery) ? t.battery + '%' + (t.charging ? ' · charging' : '') : DASH,
    ],
    ['Free storage', has(t?.freestoragebytes) ? formatBytes(t.freestoragebytes) : DASH],
    ['Uptime', has(t?.uptimeseconds) ? Math.round(t.uptimeseconds / 3600) + ' h' : DASH],
    ['Last boot', t && lastBoot ? relativeSeen(lastBoot) : DASH],
    ['Wi-Fi', wifi],
    ['IP', has(t?.ip) ? t.ip : DASH],
    ['Screen', has(t?.screenon) ? (t.screenon ? 'On' : 'Off') : DASH],
    ['Locked app', locked],
  ];

  const badges =
    t?.helperok === false || adbOn(t) || t?.profile === 'nohelper' ? (
      <>
        {t?.profile === 'nohelper' ? <StatusBadge size="sm" tone="slate" label="No helper" /> : null}
        {t?.helperok === false && t?.profile !== 'nohelper' ? <StatusBadge size="sm" tone="rose" label="Helper down" /> : null}
        {adbOn(t) ? <StatusBadge size="sm" tone="rose" label="ADB on" /> : null}
      </>
    ) : null;

  return (
    <Card title="Telemetry" actions={badges}>
      <Dl rows={rows} />
    </Card>
  );
};

const isBehind = (installed, latest) =>
  typeof installed?.code === 'number' &&
  typeof latest?.code === 'number' &&
  latest.code > installed.code;

const AppsCard = ({ device }) => {
  const installed = device.appversions || {};
  const latest = device.latestversions || {};
  const packages = Object.keys(installed);
  return (
    <Card title="Apps">
      {packages.length === 0 ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">{DASH}</p>
      ) : (
        <ul className="divide-y divide-slate-100 dark:divide-slate-700">
          {packages.map((pkg) => (
            <li key={pkg} className="flex items-center justify-between gap-3 py-2 text-sm">
              <span className="font-medium text-slate-900 dark:text-slate-100">
                {packageLabel(pkg)}
              </span>
              <span className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                {installed[pkg]?.name ? 'v' + installed[pkg].name : DASH}
                {!isNil(installed[pkg]?.code) ? ` (${installed[pkg].code})` : ''}
                {isBehind(installed[pkg], latest[pkg]) ? (
                  <StatusBadge size="sm" tone="amber" label="Outdated" />
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
};

const seedForm = (device) => ({
  name: device.name || '',
  companyid: device.store?.companyid || device.store?.company?.id || '',
  storeid: device.storeid || '',
  role: device.role || 'unassigned',
  terminalid: device.terminalid || '',
  kitchenterminalid: device.kitchenterminalid || '',
  tags: (device.tags || []).join(', '),
  notes: device.notes || '',
});

// A terminal option for the id the device already has, in case the list
// (store scoped, 100 rows) does not carry it.
const withCurrent = (options, id, name) =>
  id && name && !options.some((o) => o.value === id) ? [{ value: id, label: name }, ...options] : options;

const terminalPlaceholder = (storeid, options, noun, failed) => {
  if (!storeid) return 'Choose a store first';
  if (failed) return `Could not load ${noun}`;
  if (options && options.length === 0) return `No ${noun} in this store`;
  return 'Choose a terminal';
};

const AssignmentCard = ({ device, onDeviceChange }) => {
  const [form, setForm] = useState(() => seedForm(device));
  // null = not loaded for the current store and role
  const [terminals, setTerminals] = useState(null);
  const [kitchenTerminals, setKitchenTerminals] = useState(null);
  // A rejected read is not an empty store: the placeholder must not claim one.
  const [terminalsFailed, setTerminalsFailed] = useState(false);
  const [kitchenFailed, setKitchenFailed] = useState(false);
  const [saving, setSaving] = useState(false);

  const { storeid, role } = form;

  useEffect(() => {
    if (!storeid || role !== 'kiosk') {
      setTerminals(null);
      setTerminalsFailed(false);
      return undefined;
    }
    let cancelled = false;
    setTerminals(null);
    setTerminalsFailed(false);
    terminalApi
      .list('?storeid=' + storeid + '&pageSize=100&isdeleted=false')
      .then((res) => {
        if (!cancelled) setTerminals(toOptions(res?.data?.data));
      })
      .catch(() => {
        if (cancelled) return;
        setTerminals([]);
        setTerminalsFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [storeid, role]);

  useEffect(() => {
    if (!storeid || role !== 'kitchen') {
      setKitchenTerminals(null);
      setKitchenFailed(false);
      return undefined;
    }
    let cancelled = false;
    setKitchenTerminals(null);
    setKitchenFailed(false);
    kitchenterminalApi
      .list(storeid, '&pageSize=100')
      .then((res) => {
        if (!cancelled) setKitchenTerminals(toOptions(res?.data?.data));
      })
      .catch(() => {
        if (cancelled) return;
        setKitchenTerminals([]);
        setKitchenFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [storeid, role]);

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  // A store change clears both terminals: they belong to the old store.
  const onStoreChange = ({ companyid, storeid: nextStore }) =>
    setForm((prev) => ({
      ...prev,
      companyid,
      storeid: nextStore,
      ...(nextStore !== prev.storeid ? { terminalid: '', kitchenterminalid: '' } : {}),
    }));

  // A role change clears the terminal id the new role does not use.
  const onRoleChange = (next) =>
    set({
      role: next,
      ...(next !== 'kiosk' ? { terminalid: '' } : {}),
      ...(next !== 'kitchen' ? { kitchenterminalid: '' } : {}),
    });

  const save = async () => {
    const body = {
      name: form.name.trim(),
      // The API reads null as "unchanged" and '' as "clear" (P-28): a cleared
      // link is always an empty string, never null.
      storeid: form.storeid || '',
      role: form.role,
      terminalid: form.role === 'kiosk' ? form.terminalid || '' : '',
      kitchenterminalid: form.role === 'kitchen' ? form.kitchenterminalid || '' : '',
      tags: parseTags(form.tags),
      notes: form.notes,
    };
    setSaving(true);
    try {
      const res = await fleetApi.updateDevice(device.id, body);
      // The PUT answer lacks terminalname, kitchenterminalname and commands:
      // merge, never replace. The two names follow their ids.
      const merged = { ...device, ...res.data.data };
      merged.terminalname = merged.terminalid
        ? (terminals || []).find((o) => o.value === merged.terminalid)?.label ??
          (merged.terminalid === device.terminalid ? device.terminalname : null)
        : null;
      merged.kitchenterminalname = merged.kitchenterminalid
        ? (kitchenTerminals || []).find((o) => o.value === merged.kitchenterminalid)?.label ??
          (merged.kitchenterminalid === device.kitchenterminalid ? device.kitchenterminalname : null)
        : null;
      onDeviceChange(merged);
      message.success('Device updated');
    } catch (err) {
      message.error(fleetErrorText(err, 'Something went wrong. Try again.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card title="Assignment">
      <div className="space-y-4">
        <TWInput label="Name" value={form.name} onChange={(e) => set({ name: e.target.value })} />
        <CompanyStoreSelect
          companyId={form.companyid}
          storeId={form.storeid}
          onChange={onStoreChange}
          storeMode="single"
        />
        <TWSelect
          label="Role"
          allowClear={false}
          showSearch={false}
          options={ROLES}
          value={form.role}
          onChange={onRoleChange}
        />
        {form.role === 'kiosk' ? (
          <TWSelect
            label="Kiosk terminal"
            placeholder={terminalPlaceholder(form.storeid, terminals, 'terminals', terminalsFailed)}
            disabled={!form.storeid}
            options={withCurrent(terminals || [], form.terminalid, device.terminalname)}
            value={form.terminalid || undefined}
            onChange={(value) => set({ terminalid: value || '' })}
          />
        ) : null}
        {form.role === 'kitchen' ? (
          <TWSelect
            label="Kitchen terminal"
            placeholder={terminalPlaceholder(form.storeid, kitchenTerminals, 'kitchen terminals', kitchenFailed)}
            disabled={!form.storeid}
            options={withCurrent(
              kitchenTerminals || [],
              form.kitchenterminalid,
              device.kitchenterminalname,
            )}
            value={form.kitchenterminalid || undefined}
            onChange={(value) => set({ kitchenterminalid: value || '' })}
          />
        ) : null}
        <TWInput
          label="Tags"
          hint="Separate tags with commas"
          value={form.tags}
          onChange={(e) => set({ tags: e.target.value })}
        />
        <TWInput label="Notes" value={form.notes} onChange={(e) => set({ notes: e.target.value })} />
        <div className="flex justify-end">
          <TWButton
            variant="primary"
            onClick={save}
            loading={saving}
            disabled={saving || !form.name.trim()}
          >
            Save
          </TWButton>
        </div>
      </div>
    </Card>
  );
};

const ScreenshotCard = ({ device }) => (
  <Card title="Screenshot">
    {isHttpUrl(device.lastscreenshoturl) ? (
      <a href={device.lastscreenshoturl} target="_blank" rel="noreferrer">
        <img
          src={device.lastscreenshoturl}
          alt="Latest screenshot"
          className="max-h-64 rounded-md border border-slate-200 dark:border-slate-700"
        />
      </a>
    ) : (
      <EmptyState
        variant="dashed"
        title="No screenshot yet"
        description="Use Screenshot to capture the screen."
      />
    )}
  </Card>
);

const DeviceOverviewTab = ({ device, onDeviceChange }) => (
  <div className="grid gap-6 lg:grid-cols-2">
    <div className="min-w-0 space-y-6">
      <DeviceCard device={device} />
      <TelemetryCard device={device} />
      <AppsCard device={device} />
    </div>
    <div className="min-w-0 space-y-6">
      <AssignmentCard device={device} onDeviceChange={onDeviceChange} />
      <ScreenshotCard device={device} />
    </div>
  </div>
);

export default DeviceOverviewTab;
